import LandingTemplate from "@/components/LandingTemplate";
import { MFCalculator } from "@/components/Calculators";

export default function SIP() {
  return (
    <LandingTemplate
      audience="individual"
      seo={{
        path: "/sip-investment",
        title: "Build Wealth Through SIP",
        description: "Systematic Investment Plans turn small, regular amounts into long-term wealth. Understand SIP and step-up SIP with an illustrative calculator. Rao Investment Solutions.",
      }}
      breadcrumbs={[{ name: "Home", to: "/" }, { name: "Resources" }, { name: "Build Wealth Through SIP" }]}
      hero={{
        label: "Resource",
        title: "Build wealth, one disciplined step at a time.",
        intro: "A SIP turns investing into a habit — small, regular amounts that harness compounding and smooth out market ups and downs.",
        cta: "Design my SIP plan",
      }}
      painPoints={[
        "I can't invest a lump sum, so I keep putting it off.",
        "I try to time the market and usually get it wrong.",
        "My SIP amount hasn't changed in years, even as my income grew.",
        "I don't know if I'm investing enough for my goals.",
        "I stop my SIP whenever markets fall — exactly the wrong time.",
      ]}
      whyMatters={{
        title: "Consistency beats cleverness.",
        paras: [
          "Rupee-cost averaging means you buy more units when prices are low and fewer when high — reducing the risk of a bad entry point.",
          "Stepping up your SIP as income grows can dramatically change the outcome over long horizons.",
          "The discipline of automation removes the emotional decisions that quietly cost investors the most.",
        ],
      }}
      approach={{
        title: "A goal-linked SIP framework",
        intro: "We connect every rupee to a purpose.",
        stages: [
          { title: "Goal-map", body: "Attach each SIP to a specific goal and horizon — home, education, retirement." },
          { title: "Right-size", body: "Calculate a monthly amount (and step-up) that makes the goal realistic." },
          { title: "Diversify", body: "Spread across suitable funds by risk and horizon, not last year's winners." },
          { title: "Step-up & review", body: "Increase contributions with income and review allocation periodically." },
        ],
      }}
      solutions={[
        { name: "Equity SIPs", to: "/products/mf-pms-aif", desc: "For long-horizon goals where growth potential matters most. Subject to market risk." },
        { name: "Hybrid & Debt SIPs", to: "/products/mf-pms-aif", desc: "For medium-term goals that need a steadier ride." },
        { name: "Step-up SIP", to: "/calculators", desc: "Grow contributions each year to keep pace with income and inflation." },
      ]}
      calculator={<MFCalculator />}
      calculatorTitle="Model your SIP, step-up and withdrawals"
      howWeHelp={[
        { title: "SIP consultation", body: "We understand your goals and cash flow." },
        { title: "Personalised plan", body: "A goal-linked SIP schedule with step-ups." },
        { title: "Setup & automate", body: "We help you start and automate contributions." },
        { title: "Annual step-up", body: "We nudge and review so your SIP grows with you." },
      ]}
      whoFor={[
        "Salaried professionals with monthly surplus",
        "Investors who dislike timing the market",
        "Parents saving for education or a home",
        "Anyone who wants to invest on autopilot",
      ]}
      faq={[
        { q: "What is a step-up SIP?", a: "A step-up SIP increases your monthly contribution by a set percentage each year — often in line with income growth — which can significantly improve long-term outcomes." },
        { q: "Should I stop my SIP when markets fall?", a: "Usually not. Falling markets let your SIP buy more units cheaply. Stopping locks in the downside and misses the recovery. We help you stay the course." },
        { q: "Are the calculator results guaranteed?", a: "No. They are illustrative estimates based on the return you assume. Actual returns vary and are subject to market risk." },
      ]}
      finalCta={{ title: "Let's design a SIP that grows with you.", intro: "Book a session and we'll map your goals to a realistic, step-up SIP plan." }}
    />
  );
}
